import { useMemo, useState } from 'react';
import { ANALYTICS_CONFIG } from '../../config/analytics-config.js';
import { MODULE_PERIOD_OPTIONS, resolveModulePeriodRange } from '../constants/periods.js';
import { useOriginTagsCatalog } from './useOriginTagsCatalog.js';

/** Persiste filtros entre navegaciones SPA (por namespace de módulo). */
const filterMemory = new Map();

/**
 * Filtros comunes de módulos (período + dimensiones opcionales).
 * @param {object} [defaults]
 * @param {string} [defaults.period]
 * @param {string} [defaults.persistKey] — si se setea, recuerda el período al volver a la ruta
 * @param {boolean} [defaults.withOriginTags] — carga catálogo + selección (default Meta)
 */
export function useModuleFilters(defaults) {
  const persistKey = defaults && defaults.persistKey ? String(defaults.persistKey) : '';
  const withOriginTags = !!(defaults && defaults.withOriginTags);
  const mem = persistKey ? filterMemory.get(persistKey) : null;

  const [period, setPeriodState] = useState(
    (mem && mem.period) ||
      (defaults && defaults.period) ||
      ANALYTICS_CONFIG.defaultPeriod ||
      'last_30_days'
  );
  const [dateFrom, setDateFromState] = useState((mem && mem.dateFrom) || '');
  const [dateTo, setDateToState] = useState((mem && mem.dateTo) || '');
  const [executiveId, setExecutiveId] = useState((mem && mem.executiveId) || '');
  const [projectId, setProjectId] = useState((mem && mem.projectId) || '');
  const [channel, setChannel] = useState((mem && mem.channel) || '');
  const [status, setStatus] = useState((mem && mem.status) || '');

  const originCatalog = useOriginTagsCatalog({
    enabled: withOriginTags,
    persistKey: persistKey ? persistKey + ':origins' : 'mod:origins'
  });

  const persist = (patch) => {
    if (!persistKey) return;
    const prev = filterMemory.get(persistKey) || {};
    filterMemory.set(persistKey, { ...prev, ...patch });
  };

  const setPeriod = (v) => {
    setPeriodState(v);
    persist({ period: v });
  };
  const setDateFrom = (v) => {
    setDateFromState(v);
    persist({ dateFrom: v });
  };
  const setDateTo = (v) => {
    setDateToState(v);
    persist({ dateTo: v });
  };

  const range = useMemo(() => {
    if (period === 'custom' && dateFrom && dateTo) {
      return { dateFrom, dateTo };
    }
    return resolveModulePeriodRange(period);
  }, [period, dateFrom, dateTo]);

  return {
    period,
    setPeriod,
    periodOptions: MODULE_PERIOD_OPTIONS.concat([{ key: 'custom', label: 'Personalizado' }]),
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    executiveId,
    setExecutiveId: (v) => {
      setExecutiveId(v);
      persist({ executiveId: v });
    },
    projectId,
    setProjectId: (v) => {
      setProjectId(v);
      persist({ projectId: v });
    },
    channel,
    setChannel: (v) => {
      setChannel(v);
      persist({ channel: v });
    },
    status,
    setStatus: (v) => {
      setStatus(v);
      persist({ status: v });
    },
    originTags: originCatalog.selected,
    setOriginTags: originCatalog.setSelected,
    originTagOptions: originCatalog.options,
    originTagsLoading: originCatalog.loading,
    originTagsReady: originCatalog.ready,
    selectOriginMetaOnly: originCatalog.selectMetaOnly,
    selectOriginAll: originCatalog.selectAll,
    clearOriginTags: originCatalog.clearAll,
    range,
    filters: {
      period,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      executiveId,
      projectId,
      channel,
      status,
      originTags: originCatalog.selected
    }
  };
}
